import { Box, makeStyles, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Paper, { PaperProps } from '@material-ui/core/Paper';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import React, { useState } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';

import { FontSizes } from '../../../../core/config/mainTheme';
import { useSnackbarMessage } from '../../../../hooks/useSnackbarMessage';
import {
  useFilterDispatch,
  useFilterState,
} from '../../shared/FinancialDataContext/FinancialDataContext';
import { FinancialDataContextActionTypes } from '../../shared/FinancialDataContext/FinancialDataContext.types';
import { ActivityKeywordsChart } from './ActivityKeywordsChart';

interface Props {
  keywordsData: { name: string; value: number }[];
  isOpen: boolean;
  handleClose: () => void;
  id: number;
}

function PaperComponent(props: PaperProps) {
  const classes = useStyles();
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper classes={{ root: classes.container }} {...props} />
    </Draggable>
  );
}

export const ActivityKeywordsDialog: React.FC<Props> = ({
  keywordsData,
  isOpen,
  id,
  handleClose,
}) => {
  const [includeExclude, setIncludeExclude] = useState<'include' | 'exclude'>('include');
  const [snackBarMessage, setSnackBarMessage] = useState(false);
  const { data, appliedFilters } = useFilterState();
  const dispatch = useFilterDispatch();

  const handleIncludeExcludeChange = (event: any, newIncludeExclude: 'include' | 'exclude') => {
    if (newIncludeExclude !== null) {
      setIncludeExclude(newIncludeExclude);
    }
  };

  const onDialogClose = () => {
    setIncludeExclude('include');
    handleClose();
  };

  const onChartClick = (value: string) => {
    if (includeExclude === 'include') {
      onKeywordInclude(value);
    } else {
      onKeywordExclude(value);
    }
  };

  function onKeywordInclude(keyword: string) {
    const isExist =
      data.activityKeywords?.value?.tags?.includeValues?.includes(keyword) ||
      appliedFilters?.includes(keyword);
    if (!isExist) {
      if (!appliedFilters.includes('activityKeywords')) {
        dispatch({
          type: FinancialDataContextActionTypes.Add,
          payload: 'activityKeywords',
        });
      }
      setSnackBarMessage(true);
      dispatch({
        type: FinancialDataContextActionTypes.AddIncludeKeywordsFilterValues,
        payload: keyword,
      });
      setSnackBarMessage(false);
    }
  }

  function onKeywordExclude(keyword: string) {
    const isExist =
      data.activityKeywords?.value?.tags?.excludeValues?.includes(keyword) ||
      appliedFilters?.includes(keyword);
    if (!isExist) {
      if (!appliedFilters.includes('activityKeywords')) {
        dispatch({
          type: FinancialDataContextActionTypes.Add,
          payload: 'activityKeywords',
        });
      }
      setSnackBarMessage(true);
      dispatch({
        type: FinancialDataContextActionTypes.AddExcludeKeywordsFilterValues,
        payload: keyword,
      });
      setSnackBarMessage(false);
    }
  }

  useSnackbarMessage(
    snackBarMessage,
    'Search criteria has been updated, please click Search to refresh search results.',
  );
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <div>
      <Dialog
        maxWidth="md"
        open={isOpen}
        className={classes.container}
        onClose={onDialogClose}
        aria-labelledby="draggable-dialog-title"
        PaperComponent={PaperComponent}
      >
        <DialogTitle
          style={{ cursor: 'move' }}
          id="draggable-dialog-title"
          className={classes.dialogTitle}
        >
          {t('activityKeywords:dialog:title')}
        </DialogTitle>
        <Box className={classes.subheader}>
          <Typography style={{ padding: '0 6px 0 24px' }}>
            {t('activityKeywords:dialog:subheaderStart')}
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={includeExclude}
            onChange={handleIncludeExcludeChange}
            exclusive
          >
            <ToggleButton value="include">{t('activityKeywords:dialog:addTo')}</ToggleButton>
            <ToggleButton value="exclude">{t('activityKeywords:dialog:excludeFrom')}</ToggleButton>
          </ToggleButtonGroup>
          <Typography style={{ padding: '0 0 0 6px' }}>
            {t('activityKeywords:dialog:subheaderEnd')}
          </Typography>
        </Box>
        <DialogContent className={classes.content}>
          <ActivityKeywordsChart
            keywordsData={keywordsData}
            onMapItemClick={onChartClick}
            id={id}
            height={400}
            tooltipDisabled={false}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onDialogClose}
            color="primary"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
          >
            {t('activityKeywords:dialog:back')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    overflow: 'visible',
  },
  dialogTitle: {
    '& h2': {
      fontSize: FontSizes.modalTitleFont,
      fontWeight: 'bold',
      color: theme.palette.primary.dark,
    },
  },
  content: {
    height: '100%',
    overflow: 'visible',
  },
  subheader: {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      fontSize: FontSizes.size.normal,
      fontWeight: 'bold',
      color: theme.palette.text.secondary,
    },
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontSize: FontSizes.size.normal,
      fontWeight: 'bold',
      backgroundColor: theme.palette.grey[300],
    },
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    '& .MuiToggleButtonGroup-grouped:not(:last-child)': {
      borderTopLeftRadius: '20px',
      borderBottomLeftRadius: '20px',
    },
    '& .MuiToggleButtonGroup-grouped:not(:first-child)': {
      borderTopRightRadius: '20px',
      borderBottomRightRadius: '20px',
    },
  },
}));
